/**
 * Date formatting utilities
 * Formats dates according to design system requirements
 */

/**
 * Format date string from backend (YYYY-MM-DD HH:MM:SS) to "MMM DD, YYYY" format
 * Example: "2024-08-16 14:30:00" -> "Aug 16, 2024"
 */
export const formatDateAdded = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // Backend sends UTC strings without a timezone indicator (e.g. "2026-05-24 16:52:00").
    // Append "Z" so JavaScript parses as UTC and toLocaleString converts to the browser's local timezone.
    const utcString = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing fails
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Parse a backend UTC datetime string into the local Date (shared helper).
const parseBackendDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const utcString =
    dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
  const date = new Date(utcString);
  return isNaN(date.getTime()) ? null : date;
};

/** Just the date: "Jun 21, 2026" */
export const formatDateOnly = (dateString: string): string => {
  const date = parseBackendDate(dateString);
  if (!date) return dateString || '';
  return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Just the time: "12:57 PM" */
export const formatTimeOnly = (dateString: string): string => {
  const date = parseBackendDate(dateString);
  if (!date) return '';
  return date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
};

/**
 * How long processing took: the gap between upload and processed.
 * Returns e.g. "1m 12s", "45s", or "" if either timestamp is missing.
 */
export const formatProcessingTime = (
  uploadedString: string,
  processedString?: string | null,
): string => {
  if (!processedString) return '';
  const start = parseBackendDate(uploadedString);
  const end = parseBackendDate(processedString);
  if (!start || !end) return '';
  let secs = Math.round((end.getTime() - start.getTime()) / 1000);
  if (secs < 0) secs = 0;
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};
